import { ConfigStateService } from '@abp/ng.core';
import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { CustomSettingsService } from '../proxy/custom-settings.service';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  ADD_ON_AI_FEATURE_MODEL,
  ADD_ON_AI_FEATURE_LLM_CONFIGURATION,
  ADD_ON_AI_FEATURE_MODEL_NAME,
} from './addon-ai-features-settings.consts';
import { CustomSettingsDto } from '../proxy/custom-settings.model';
import { AddOnAIFeaturesSettingsService } from './proxy/addon-ai-features-settings.service';
import { LLM_MODELS } from 'src/app/shared/shared.consts';
import { LlmConfigurationProvider } from 'src/app/integrations/proxy/integration.model';
import { finalize } from 'rxjs/operators';
import { CUSTOM_MODEL } from 'src/app/ai-agents/proxy/ai-agents.consts';

@Component({
  selector: 'cai-addon-ai-features-settings',
  templateUrl: './addon-ai-features-settings.component.html',
  styleUrls: ['./addon-ai-features-settings.component.scss'],
  standalone: false,
})
export class AddOnAIFeaturesSettingsComponent implements OnInit {
  form: UntypedFormGroup;
  llmConfigurationOptions;
  modelOptions;
  selectedLlmConfigurationId: string;
  selectedLlmConfiguration;
  selectedModel: string;
  isCustomModelSelected = false;
  useModelOptions = true;
  modelNameMaxLength = 200;
  isLoading = false;

  constructor(
    private config: ConfigStateService,
    private fb: UntypedFormBuilder,
    private settingsService: CustomSettingsService,
    private toasterService: ToasterService,
    private addOnAIFeaturesSettingsService: AddOnAIFeaturesSettingsService,
  ) {}

  ngOnInit(): void {
    this.addOnAIFeaturesSettingsService.getAddOnAiFeatureSettingOptions().subscribe((options) => {
      this.llmConfigurationOptions = options.llmConfigurationSettings
        .filter((opt) => opt.provider !== LlmConfigurationProvider.vertexAi) // Remove this filter when the Generative AI service supports vertex AI provider.
        .map((opt) => ({
          ...opt,
          models:
            opt.provider === LlmConfigurationProvider.sestek
              ? [] // Remove this condition when the Generative AI service supports fetching Vllm parameters from the API.
              : (LLM_MODELS.get(opt.provider) ?? []),
        }));
      this.buildForm();
    });
  }

  buildForm() {
    this.selectedLlmConfigurationId = this.config.getSetting(ADD_ON_AI_FEATURE_LLM_CONFIGURATION);
    this.getModelOptions();

    this.form = this.fb.group({
      llmConfiguration: [this.selectedLlmConfigurationId, [Validators.required]],
      modelName: [
        this.config.getSetting(ADD_ON_AI_FEATURE_MODEL_NAME),
        [Validators.maxLength(this.modelNameMaxLength)],
      ],
    });

    if (this.useModelOptions) {
      this.form.addControl(
        'model',
        this.fb.control(this.config.getSetting(ADD_ON_AI_FEATURE_MODEL)?.trim() || '', [
          Validators.required,
        ]),
      );
    }

    this.selectedModel = this.form.get('model')?.value;
    this.checkCustomModelSelection();
  }

  submitForm() {
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;

    const modelValue = this.form.get('model')?.value?.trim();
    const modelNameValue = this.form.get('modelName')?.value?.trim();

    const settings: CustomSettingsDto[] = [
      {
        name: ADD_ON_AI_FEATURE_LLM_CONFIGURATION,
        value: this.form.get('llmConfiguration').value,
      },
      {
        name: ADD_ON_AI_FEATURE_MODEL,
        value: modelValue ? modelValue : null,
      },
      {
        name: ADD_ON_AI_FEATURE_MODEL_NAME,
        value: this.isCustomModelSelected && modelNameValue ? modelNameValue : null,
      },
    ];

    this.settingsService
      .save(settings)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.toasterService.success('::CustomSettings:Save:Success');
          this.buildForm();
        },
        error: (error) => {
          const businessExceptionErrorCodePrefix = 'Raven:';

          // If the error is caused by a business exception, then the related error message will be shown
          // via global error handling popup, so no need to display an additional error toaster here.
          if (!error?.error?.error?.code?.startsWith(businessExceptionErrorCodePrefix)) {
            this.toasterService.error('::CustomSettings:Save:Error');
            console.log(error);
          }
        },
      });
  }

  setSelectedLlmConfiguration(id: string) {
    this.selectedLlmConfigurationId = id;
    this.getModelOptions();
    this.selectedModel = '';
    this.isCustomModelSelected = false;
    this.resetFormControl(this.form.get('modelName'));

    if (!this.useModelOptions) {
      this.form.removeControl('model');
    } else {
      const existing = this.form.get('model');
      if (existing) {
        this.resetFormControl(existing, [Validators.required]);
      } else {
        this.form.addControl('model', this.fb.control('', [Validators.required]));
      }
    }

    this.checkCustomModelSelection();
  }

  setSelectedModel() {
    this.selectedModel = this.form.get('model')?.value;
    this.resetFormControl(this.form.get('modelName'));
    this.checkCustomModelSelection();
  }

  checkCustomModelSelection() {
    const control = this.form.get('modelName');

    if (!this.selectedLlmConfiguration || !this.selectedModel) {
      this.isCustomModelSelected = false;
      this.resetFormControl(control);
      return;
    }

    const hasModels = this.selectedLlmConfiguration.models.length > 0;
    this.isCustomModelSelected = hasModels && this.selectedModel === CUSTOM_MODEL;

    if (this.isCustomModelSelected) {
      control.setValidators([Validators.required, Validators.maxLength(this.modelNameMaxLength)]);
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity();
  }

  getModelOptions() {
    this.selectedLlmConfiguration = this.llmConfigurationOptions.find(
      (x) => x.id === this.selectedLlmConfigurationId,
    );

    this.modelOptions = this.selectedLlmConfiguration?.models ?? [];
    this.useModelOptions = this.modelOptions.length > 0;
  }

  private resetFormControl(control: AbstractControl, validators?: ValidatorFn[]) {
    control.reset();
    control.setValue('');
    control.clearValidators();
    if (validators) {
      control.setValidators(validators);
    }
    control.updateValueAndValidity();
  }
}
